"use client";

/**
 * @file 图片上传组件
 * @description 提供拖放和点击上传图片功能的组件
 * 支持图片预览和删除
 */

import { useState, useRef, useCallback } from 'react';
import Image from 'next/image';

/**
 * 图片上传组件属性接口
 */
interface ImageUploaderProps {
  /** 图片上传成功回调函数 */
  onImageUpload: (imageDataUrl: string) => void;
}

/**
 * 图片上传组件
 * @param {ImageUploaderProps} props - 组件属性
 * @returns {JSX.Element} 图片上传组件
 */
export default function ImageUploader({ onImageUpload }: ImageUploaderProps) {
  // 状态管理
  const [isDragging, setIsDragging] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  // 文件输入引用
  const fileInputRef = useRef<HTMLInputElement>(null);

  /**
   * 处理图片文件
   * @param {File} file - 上传的图片文件
   */
  const handleImageFile = useCallback((file: File) => {
    // 检查文件类型
    if (!file.type.startsWith('image/')) {
      alert('请上传图片文件');
      return;
    }

    // 创建文件读取器
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      setPreviewImage(dataUrl);
      onImageUpload(dataUrl);
    };
    reader.readAsDataURL(file);
  }, [onImageUpload]);

  /**
   * 处理拖拽进入事件
   * @param {React.DragEvent} e - 拖拽事件
   */
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  /**
   * 处理拖拽离开事件
   * @param {React.DragEvent} e - 拖拽事件
   */
  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  /**
   * 处理拖拽悬停事件
   * @param {React.DragEvent} e - 拖拽事件
   */
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) {
      setIsDragging(true);
    }
  }, [isDragging]);

  /**
   * 处理拖拽放下事件
   * @param {React.DragEvent} e - 拖拽事件
   */
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      handleImageFile(file);
    }
  }, [handleImageFile]);

  /**
   * 处理文件选择事件
   * @param {React.ChangeEvent<HTMLInputElement>} e - 文件选择事件
   */
  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      handleImageFile(file);
    }
  }, [handleImageFile]);

  /**
   * 触发文件选择对话框
   */
  const triggerFileInput = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  /**
   * 移除已上传的图片
   */
  const removeImage = useCallback(() => {
    setPreviewImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onImageUpload('');
  }, [onImageUpload]);

  return (
    <div className="w-full">
      {/* 隐藏的文件输入框 */}
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept="image/*"
        onChange={handleFileSelect}
      />

      {/* 图片预览区域 */}
      {previewImage ? (
        <div className="relative">
          <div className="relative w-full h-64 rounded-lg overflow-hidden">
            <Image
              src={previewImage}
              alt="Uploaded pet image"
              fill
              className="object-contain"
            />
          </div>
          <button
            onClick={removeImage}
            className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600 transition-colors"
            aria-label="Remove image"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      ) : (
        /* 拖放区域 */
        <div
          className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
            isDragging
              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
              : 'border-gray-300 dark:border-gray-700 hover:border-blue-400 dark:hover:border-blue-600'
          }`}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={triggerFileInput}
        >
          <div className="flex flex-col items-center justify-center space-y-3">
            <svg
              className={`h-12 w-12 ${
                isDragging ? 'text-blue-500' : 'text-gray-400'
              }`}
              stroke="currentColor"
              fill="none"
              viewBox="0 0 48 48"
              aria-hidden="true"
            >
              <path
                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <span className="font-medium text-blue-600 dark:text-blue-400">
                Click to upload
              </span>{' '}
              or drag and drop
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-500">
              PNG, JPG, GIF formats supported, max 10MB
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
